import React, { useCallback, useMemo } from 'react';
import { shuffle, partial } from 'lodash';
import SectionDividers from '@dyl/components/SectionDividers';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import RubricBlock from '@dyl/blocks/RubricBlock';
import { Grid, IconButton, Tooltip, Typography, TextField, Box, FormControl, FilledInput, InputLabel } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Shuffle as ShuffleIcon } from '@mui/icons-material';
import Answer from './Answer';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ImagePicker from '@dyl/components/ImagePicker';
import { limitWordsAndSpaces } from '../../../utils/limitWordsAndSpaces';
import PropTypes from 'prop-types';
const useStyles = makeStyles(theme => ({
  root: { maxWidth: theme.breakpoints.values.md },
  groupHeader: { marginBottom: '16px', textAlign: 'center' },
}));

export default function InformationTextEditor() {
  const classes = useStyles();
  const [items] = useTaskField('items');


  const exampleItem = items.find(x => x.is_example);


  return (
    <div className={classes.root}>
      <SectionDividers>
        {exampleItem && (
          <ExampleItem index={exampleItem.index} />)}
        <PageItem pageIndex={1} title="Page 1" gapCount={3} />
        <PageItem pageIndex={2} title="Page 2" gapCount={4} />
      </SectionDividers>
    </div>
  );
}

function ExampleItem({ index }) {
  const useField = partial(useTaskField, 'items', index);
  const [item, setItem] = useField();
  const isExample = item.is_example;

  const imageId = item.background_image || null;
  const description = item.stimulus_text || '';

  const answers = useMemo(() => {
    if (!item.answers) {
      return [
        { text: '', display_order: 1, is_correct_answer: true },
        { text: '', display_order: 2, is_correct_answer: false },
        { text: '', display_order: 3, is_correct_answer: false },
      ];
    }

    return item.answers.map((a, idx) =>
      typeof a === 'string'
        ? { text: a, display_order: idx + 1, is_correct_answer: idx === 0 }
        : a
    );
  }, [item.answers]);

  const setImageId = (imgId) => setItem({ ...item, background_image: imgId });
  const setDescription = (desc) => setItem({ ...item, stimulus_text: desc });

  const handleAnswerChange = (idx) => (e) => {
    const updated = answers.map((ans, i) =>
      i === idx ? { ...ans, text: limitWordsAndSpaces(ans.text, 10, e.target.value, 'A2') } : ans
    );
    setItem({ ...item, answers: updated });
  };


  if (!isExample) return null;

  return (
    <>
      <Typography sx={{
        color: 'var(--black, #000)',
        fontFamily: 'Roboto',
        fontSize: '26px',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal'
      }}>
        Example
      </Typography>
      <div>
        <RubricBlock />
        <Typography sx={{
          color: '#323232',
          fontSize: '18px',
          fontStyle: 'normal',
          fontWeight: 'bold',
          height: '24px',
          marginTop: '22px',
          marginBottom: '16px'
        }}>
          Background Image
        </Typography>
        <ImagePicker
          label="Example background image"
          value={imageId}
          onChange={setImageId}
          fullWidth
        />
      </div>
      <div>
        <Typography sx={{
          color: '#323232',
          fontSize: '18px',
          fontStyle: 'normal',
          fontWeight: 'bold',
          height: '24px',
          marginBottom: '4px',
          width: '330px',
          marginTop: '22px'
        }}>
          Example stimulus text
        </Typography>
        <Typography sx={{
          color: '#323232',
          fontFamily: 'Roboto',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 400,
          width: '468px',
          height: '32px',
          lineHeight: 'normal',
          marginBottom: '6px'
        }}>
          Add an underscore _ to indicate where the gap is. The example stimulus text must have one gap and a maximum of 20 words including the gap.
        </Typography>
        <Box sx={{ position: 'relative', width: '660px', maxWidth: '100%', marginBottom: 2 }}>
          <TextField
            multiline
            minRows={3}
            maxRows={6}
            variant="filled"
            fullWidth
            value={description}
            onChange={e => {
              setDescription(limitWordsAndSpaces(description, 20, e.target.value, 'A2'));
            }}
            sx={{
              backgroundColor: '#F2F1F0',
              borderRadius: '12px',
              width: '660px',
              height: '112px',
              '& .MuiFilledInput-root': {
                borderRadius: '12px 12px 0 0',
                fontSize: '16px',
                padding: '16px',
                height: '112px',
                boxSizing: 'border-box',
              },
            }}
            inputProps={{
              style: { background: 'transparent' }
            }}
          />
          <Typography
            sx={{
              position: 'absolute',
              right: 16,
              bottom: 8,
              fontSize: '12px',
              color: description.trim().split(/\s+/).length > 20 ? 'red' : '#323232',
              opacity: 0.7,
            }}
          >
            Word count {description.trim() ? description.trim().split(/\s+/).length : 0}/20
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 1 }}>
          <Typography sx={{
            color: '#323232',
            fontSize: '18px',
            fontStyle: 'normal',
            fontWeight: 'bold',
            height: '24px',
            marginBottom: '10px',
            width: '330px',
          }}>
            Example answers
          </Typography>
        </Box>
        <Typography sx={{
          color: '#323232',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: 'normal',
          height: '24px',
          marginBottom: '20px',
          width: '330px'
        }}>
          Type to add and delete answers and distractors.
          Character limit: <b>10</b>
        </Typography>
        <Typography
          sx={{
            color: '#323232',
            fontFamily: 'Roboto',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: 'normal',
            marginBottom: '6px',
          }}
        >
          Gap 1
        </Typography>
        <Grid container sx={{ marginBottom: '20px', width: '200px', gap: '2px' }}>
          {answers.map((ans, idx) => (
            <Grid item xs={12} key={idx}>
              <FormControl
                variant="filled"
                fullWidth
                sx={{ backgroundColor: '#F2F1F0', borderRadius: '8px', width: '200px' }}
              >
                <InputLabel htmlFor={`answer-${idx}`}>
                  {idx === 0 ? 'Answer A' : 'Distractor'}
                </InputLabel>

                <FilledInput
                  id={`answer-${idx}`}
                  value={ans.text}
                  onChange={handleAnswerChange(idx)}
                  inputProps={{ maxLength: 10 }}
                />
              </FormControl>

            </Grid>
          ))}
        </Grid>
      </div>
    </>
  );
}
ExampleItem.propTypes = {
  index: PropTypes.number.isRequired
}


function PageItem({ pageIndex, title, gapCount, index }) {
  const [items, setItems] = useTaskField("items");

  const filteredItems = items.slice(1);

  const startIndex = pageIndex === 1 ? 0 : 3;
  const endIndex = startIndex + gapCount;

  const pageItems = filteredItems.slice(startIndex, endIndex);

  const description = pageItems[0]?.stimulus_text || "";
  const imageId2 = pageItems[0]?.background_image || null;

  const setDescription = (desc) => {
    const updated = items.map((it, idx) => {
      if (idx === 0) return it;
      if (pageIndex === 1 && idx >= 1 && idx <= 3) {
        return { ...it, stimulus_text: desc };
      }
      if (pageIndex === 2 && idx >= 4 && idx <= 7) {
        return { ...it, stimulus_text: desc };
      }
      return it;
    });
    setItems(updated);
  };

  const setImageId = (imageId2) => {
    const updated = items.map((it, idx) => {


      if (pageIndex === 2 && idx >= 4 && idx <= 7) {
        return { ...it, background_image: imageId2 };
      }

      return it;
    });
    setItems(updated);
  };

  const displayOrders = useMemo(
    () =>
      pageItems
        .flatMap((item) =>
          item.answers.map((a, i) => ({
            index: item.index,
            answerIndex: i,
            displayOrder: a.display_order,
            isDistractor: !a.is_correct_answer,
            gapIndex: item.index,
          }))
        )
        .sort((a, b) => a.displayOrder - b.displayOrder),
    [pageItems]
  );

  const resetOrder = useCallback(() => {
    let orderIndex = 1;
    const updated = items.map(item => {
      if (item.page !== pageIndex) return item;
      return {
        ...item,
        answers: item.answers.map(ans => ({
          ...ans,
          display_order: orderIndex++,
        })),
      };
    });
    setItems(updated);
  }, [items, pageIndex, setItems]);


  const shuffleOrder = useCallback(() => {
    const updated = items.map(item => {
      if (item.page !== pageIndex) return item;
      const displayOrders = item.answers.map(ans => ans.display_order);
      const shuffledOrders = shuffle(displayOrders);
      return {
        ...item,
        answers: item.answers.map((ans, idx) => ({
          ...ans,
          display_order: shuffledOrders[idx],
        })),
      };
    });

    setItems(updated);
  }, [items, pageIndex, setItems]);


  const pageStimulustWordCount = pageIndex === 1 ? 45 : 65;

  return (
    <>
      <Typography sx={{
        color: 'var(--black, #000)',
        fontFamily: 'Roboto',
        fontSize: '26px',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal',
        marginBottom: '6px'
      }}>
        {title}
      </Typography>
      {pageIndex === 2 && <div>
        <Typography sx={{
          color: '#323232',
          fontSize: '18px',
          fontStyle: 'normal',
          fontWeight: 'bold',
          height: '24px',
          marginTop: '22px',
          marginBottom: '16px'
        }}>
          Background Image
        </Typography>
        <ImagePicker
          label="Background image"
          value={imageId2}
          onChange={setImageId}
          fullWidth
        />
      </div>}
      <div>
        <Typography sx={{
          color: '#323232',
          fontSize: '18px',
          fontStyle: 'normal',
          fontWeight: 'bold',
          height: '24px',
          marginBottom: '4px',
          width: '330px',
          marginTop: '22px'
        }}>
          Stimulus text
        </Typography>
        <Typography sx={{
          color: '#323232',
          fontFamily: 'Roboto',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 400,
          width: '468px',
          height: '32px',
          lineHeight: 'normal',
          marginBottom: '6px'
        }}>
          Add an underscore _ to indicate where the gaps are.{pageIndex === 1 ? 'Page 1 ' : 'Page 2 '} text must have {pageIndex === 1 ? 'three ' : 'four '} gaps and a maximum of {pageStimulustWordCount} words including the gaps.
        </Typography>
        <Box sx={{ position: 'relative', width: '660px', maxWidth: '100%', marginBottom: 2 }}>
          <TextField
            multiline
            minRows={3}
            maxRows={6}
            variant="filled"
            fullWidth
            value={description}
            onChange={e => {
              setDescription(limitWordsAndSpaces(description, pageStimulustWordCount, e.target.value, 'A2'));
            }}
            sx={{
              backgroundColor: '#F2F1F0',
              borderRadius: '12px',
              width: '660px',
              minHeight: '112px',
              '& .MuiFilledInput-root': {
                borderRadius: '12px 12px 0 0',
                fontSize: '16px',
                padding: '16px',
                minHeight: '112px',
                boxSizing: 'border-box',
              },
            }}
            inputProps={{
              style: { background: 'transparent' }
            }}
          />
          <Typography
            sx={{
              position: 'absolute',
              right: 16,
              bottom: 8,
              fontSize: '12px',
              color: description.trim().split(/\s+/).length > { pageStimulustWordCount } ? 'red' : '#323232',
              opacity: 0.7,
            }}
          >
            Word count {description.trim() ? description.trim().split(/\s+/).length : 0}/{pageStimulustWordCount}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 1 }}>
          <Typography sx={{
            color: '#323232',
            fontSize: '18px',
            fontStyle: 'normal',
            fontWeight: 'bold',
            height: '24px',
            marginBottom: '10px',
            width: '330px',
          }}>
            Answers
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
          <Typography sx={{
            color: '#323232',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: 'normal',
            height: '52px',
            marginBottom: '20px',
            width: '330px'
          }}>
            Drag the handles to change display order.<br />
            Type to add and delete answers and distractors. <br />
            Character limit: <b>10</b>
          </Typography>
          <Box sx={{ position:'relative', top:'-6px' }}>
            <Tooltip title="Reset Order">
              <IconButton onClick={resetOrder} size="small">
                <AccessTimeIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Shuffle Order">
              <IconButton onClick={shuffleOrder} size="small">
                <ShuffleIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

      </div>

      <Grid container spacing={1}>
        {pageItems.map((item, gapIdx) => {
          const gapAnswers = displayOrders.filter((a) => a.index === item.index);

          return (
            <Grid item xs={3} key={item.index}>
              <Typography
                sx={{
                  color: '#323232',
                  fontFamily: 'Roboto',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: 'normal',
                  marginBottom: '6px',
                }}
              >
                Gap {pageIndex === 1 ? gapIdx + 2 : gapIdx + 5}
              </Typography>
              {gapAnswers.map(({ index, answerIndex, displayOrder, isDistractor }) => (
                <Answer
                  key={20 * index + answerIndex}
                  ELEMENT_COUNT={gapAnswers.length}
                  index={index}
                  answerIndex={answerIndex}
                  isDistractor={isDistractor}
                  displayOrder={displayOrder}
                  list={gapAnswers}
                  pageIndex={pageIndex}
                />
              ))}
            </Grid>
          );
        })}
      </Grid>
    </>
  );
}
PageItem.propTypes = {
  pageIndex: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  gapCount: PropTypes.number.isRequired,
  index: PropTypes.number
}